import { useState, useEffect, useRef } from 'react';
import { Typography, Input, Button, Form, Flex, Radio } from 'antd';
import styled from 'styled-components';
import { ReactComponent as IconLogo } from '../../../assets/icons/icon-logo.svg';
import { ReactComponent as IconPhone } from '../../../assets/icons/icon-phone.svg';
import { ReactComponent as IconSocialTelegram } from '../../../assets/icons/social/icon-social-telegram.svg';
import { ReactComponent as IconSocialViber } from '../../../assets/icons/social/icon-social-viber.svg';
import { ReactComponent as IconSocialVk } from '../../../assets/icons/social/icon-social-vk.svg';
import useAuth from '../../../hooks/useAuth';

const { Title, Text, Link } = Typography;

const LoginForm = () => {
  const {
    sendCodeStatus,
    phoneNumber,
    remainingCooldown,
    sendCode,
    resendCode,
    verifyCode,
    isCooldownExpired,
    setPhoneNumber,
  } = useAuth();

  const [form] = Form.useForm();
  const [isPhoneEntered, setIsPhoneEntered] = useState(false);
  const [isShowCode, setIsShowCode] = useState(false);
  const [code, setCode] = useState(['', '', '', '']);
  const [sendCodeBy, setSendCodeBy] = useState('Telegram');
  const inputs = useRef([]);

  // Форматирование номера телефона
  const formatPhoneNumber = (input) => {
    if (input === null || input === undefined) return '';
    const strInput = String(input);

    // Извлекаем только цифры и знак +
    let cleaned = strInput.replace(/[^\d+]/g, '');

    // Если пустая строка после очистки
    if (!cleaned) return '';

    // Удаляем все знаки + кроме первого
    const plusCount = (cleaned.match(/\+/g) || []).length;
    if (plusCount > 1) {
      const firstPlusIndex = cleaned.indexOf('+');
      cleaned = cleaned[firstPlusIndex] + cleaned.replace(/\+/g, '');
    }

    // Нормализация: приводим к формату +7XXXXXXXXXX
    let normalized = '';
    
    // Если начинается с +
    if (cleaned.startsWith('+')) {
      const digits = cleaned.substring(1);
      
      // Если после + идет 8, заменяем на 7
      if (digits.startsWith('8')) {
        normalized = '+7' + digits.substring(1);
      }
      // Если после + идет 7
      else if (digits.startsWith('7')) {
        normalized = '+' + digits;
      }
      // Если после + другая цифра, добавляем 7
      else if (digits.length > 0) {
        normalized = '+7' + digits;
      }
      // Если только +
      else {
        normalized = '+7';
      }
    }
    // Если начинается с 8
    else if (cleaned.startsWith('8')) {
      normalized = '+7' + cleaned.substring(1);
    }
    // Если начинается с 7
    else if (cleaned.startsWith('7')) {
      normalized = '+' + cleaned;
    }
    // Если начинается с другой цифры
    else {
      normalized = '+7' + cleaned;
    }
    
    // Ограничиваем длину: +7 + 10 цифр максимум
    if (normalized.length > 12) {
      normalized = normalized.substring(0, 12);
    }
    
    // Форматирование: +7 XXX XXX XX XX
    if (normalized.startsWith('+7')) {
      const digits = normalized.substring(2);
      let formatted = '+7';
      
      if (digits.length > 0) {
        formatted += ' ' + digits.substring(0, 3);
      }
      if (digits.length > 3) {
        formatted += ' ' + digits.substring(3, 6);
      }
      if (digits.length > 6) {
        formatted += ' ' + digits.substring(6, 8);
      }
      if (digits.length > 8) {
        formatted += ' ' + digits.substring(8, 10);
      }
      
      return formatted;
    }
    
    return normalized;
  };

  // Получение чистого номера (только цифры с +7)
  const getCleanPhoneNumber = (formatted) => {
    if (formatted === null || formatted === undefined) return '';
    return String(formatted).replace(/[^\d+]/g, '');
  };

  // Обработка изменения номера телефона
  const handlePhoneChange = (e) => {
    const inputValue = e.target.value;
    const formattedValue = formatPhoneNumber(inputValue);
    const cleanValue = getCleanPhoneNumber(formattedValue);
    
    setIsPhoneEntered(cleanValue.length === 12); // +7 и 10 цифр
    form.setFieldsValue({ phone: formattedValue });
  };

  // Обработка вставки из буфера обмена
  const handlePhonePaste = (e) => {
    e.preventDefault();
    
    // Получаем вставляемый текст
    const pastedText = e.clipboardData.getData('text');
    
    // Очищаем от всех символов кроме цифр и +
    const cleanedText = pastedText.replace(/[^\d+]/g, '');
    
    // Получаем текущее значение и позицию курсора
    const input = e.target;
    const currentValue = input.value;
    const cursorStart = input.selectionStart;
    const cursorEnd = input.selectionEnd;
    
    // Формируем новое значение с учетом вставки
    const newValue = currentValue.slice(0, cursorStart) + cleanedText + currentValue.slice(cursorEnd);
    
    // Форматируем и устанавливаем
    const formattedValue = formatPhoneNumber(newValue);
    const cleanValue = getCleanPhoneNumber(formattedValue);
    
    setIsPhoneEntered(cleanValue.length === 12);
    form.setFieldsValue({ phone: formattedValue });
  };

  // Обработка нажатий клавиш для телефона
  const handlePhoneKeyDown = (e) => {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];
    
    // Разрешаем Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if (e.ctrlKey && ['a', 'c', 'v', 'x', 'z', 'ф', 'с', 'м', 'ч', 'я'].includes(e.key.toLowerCase())) {
      return;
    }
    
    // Обработка Backspace для +7
    if (e.key === 'Backspace') {
      const currentValue = e.target.value;
      const cursorPosition = e.target.selectionStart;
      
      // Если курсор после "7" в "+7" или после "+"
      if (currentValue.startsWith('+7') && cursorPosition <= 2) {
        e.preventDefault();
        form.setFieldsValue({ phone: '' });
        setIsPhoneEntered(false);
        return;
      }
    }
    
    // Разрешаем цифры и знак +
    if (allowedKeys.includes(e.key) || /[\d+]/.test(e.key)) {
      return;
    }
    
    e.preventDefault();
  };

  // Отправка кода на телефон
  const onFinishPhone = async (values) => {
    try {
      const cleanPhone = getCleanPhoneNumber(values.phone);
      const data = {
        phone: cleanPhone,
        SendCodeBy: sendCodeBy
      };
      await sendCode(data);
      setIsShowCode(true);
    } catch (error) {
      console.error('Ошибка отправки кода. Проверьте номер телефона');
    }
  };

  // Повторная отправка кода
  const handleResendPhone = async () => {
    try {
      await resendCode();
    } catch (error) {
      console.error('Ошибка повторной отправки кода');
    }
  };

  // Отправка кода верификации
  const sendVerificationCode = async (verificationCode) => {
    try {
      await verifyCode(verificationCode);
    } catch (error) {
      console.error('Неверный код. Попробуйте еще раз');
      // Очищаем поля при неверном коде
      setCode(['', '', '', '']);
      if (inputs.current[0]) {
        inputs.current[0].focus();
      }
    }
  };

  // Обработка изменения кода
  const handleChange = (index, value) => {
    // Разрешаем только цифры
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Автоматический переход к следующему полю
    if (value !== '' && index < code.length - 1) {
      inputs.current[index + 1].focus();
    }

    // Автоматическая отправка при заполнении всех полей
    if (index === code.length - 1 && value !== '') {
      const fullCode = newCode.join('');
      if (fullCode.length === 4) {
        sendVerificationCode(fullCode);
      }
    }
  };

  // Обработка клавиши Backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (code[index] === '' && index > 0) {
        e.preventDefault();
        inputs.current[index - 1].focus();
      } else if (code[index] !== '') {
        // Очищаем текущее поле
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
      }
    }
  };

  // Возврат к вводу телефона
  const handleBackToPhone = () => {
    setIsShowCode(false);
    setCode(['', '', '', '']);
    setPhoneNumber('');
    form.resetFields();
    setIsPhoneEntered(false);
  };

  // Эффект для фокуса на первом поле кода
  useEffect(() => {
    if (isShowCode && inputs.current[0]) {
      inputs.current[0].focus();
    }
  }, [isShowCode]);

  return (
    <Wrapper>
      <AuthForm>
        <FormHeader>
          <IconLogo/>
          <Text style={{fontSize: '18px'}}>еда на каждый день</Text>
        </FormHeader>
        <FormBody>
          <Title level={2}>Личный кабинет партнёра</Title>
          <Form
            form={form}
            layout='vertical'
            name="phone_auth_form"
            initialValues={{ remember: true }}
            onFinish={isShowCode ? 'onFinishCode' : onFinishPhone}
          >
            {!isShowCode && (
              <>
                <Form.Item
                  name="phone"
                  label="Номер телефона"
                  rules={[
                    { 
                      required: true, 
                      message: 'Пожалуйста, введите номер телефона' 
                    },
                    {
                      validator: (_, value) => {
                        const cleaned = getCleanPhoneNumber(value);
                        if (cleaned.length === 12 && cleaned.startsWith('+7')) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Некорректный номер телефона'));
                      }
                    }
                  ]}
                >
                  <Input 
                    style={{padding: '15px 25px', borderRadius: '16px', fontSize: '18px'}}
                    placeholder="+7 --- --- -- --"
                    maxLength={16}
                    onChange={handlePhoneChange}
                    onKeyDown={handlePhoneKeyDown}
                    onPaste={handlePhonePaste}
                  />
                </Form.Item>

                <Form.Item>
                  <Radio.Group 
                    value={sendCodeBy}
                    optionType="default"
                    onChange={(e) => setSendCodeBy(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <Radio.Button
                      value="Telegram" 
                    >
                      Telegram
                    </Radio.Button>
                    <Radio.Button 
                      value="SMS" 
                    >
                      SMS
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>

                <Button
                  style={{marginBottom: '20px', padding: '15px 25px', width: '100%', height: '60px', borderRadius: '16px', fontSize: '18px'}}
                  type="primary"
                  htmlType="submit"
                  loading={sendCodeStatus === 'pending'}
                  disabled={!isPhoneEntered || sendCodeStatus === 'pending'}
                >
                  Выслать код
                </Button>
              </>
            )}
            {isShowCode && (
              <>
                <Form.Item
                  name="code"
                  label={`Код отправили сообщением на номер ${phoneNumber}`}
                >
                  <div style={{display: 'flex', justifyContent: 'center'}}>
                    {code.map((value, index) => (
                      <Input
                        key={index}
                        ref={(input) => (inputs.current[index] = input)}
                        value={value}
                        maxLength={1}
                        onChange={(e) => handleChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        style={{padding: '10px 15px', borderRadius: '16px', fontSize: '24px', fontWeight: 'bold', width: '50px', marginRight: '10px' }}
                      />
                    ))}
                  </div>
                </Form.Item>
                <Button
                  style={{marginBottom: '20px', padding: '15px 25px', width: '100%', height: '60px', borderRadius: '16px', fontSize: '14px'}}
                  type="primary"
                  disabled={!isCooldownExpired()}
                  onClick={handleResendPhone}
                >
                  {isCooldownExpired() ? 'Получить новый код' : `Получить новый код через ${remainingCooldown} секунд`}
                </Button>
                <Button
                  style={{marginBottom: '20px', padding: '15px 25px', width: '100%', height: '60px', borderRadius: '16px', fontSize: '14px'}}
                  type="default"
                  onClick={handleBackToPhone}
                >
                  Назад к вводу телефона
                </Button>
              </>
            )}
          </Form>
          {/* <div><Text>Продолжая, вы принимаете условия</Text></div>
          <div><a href='https://api.menuforme.online/files/146923/static/userpolicy.html?time=164396005052412' style={{color: '#6F9462'}}>пользовательского соглашения</a></div> */}
        </FormBody>
        <FormFooter>
          <Title level={5}>Техническая поддержка</Title>
          <StyledLink href={'tel:+7-800-200-69-24'}><IconPhone/><Text style={{fontSize: '14px'}}>+7-800-200-69-24</Text></StyledLink>
          <Flex gap='8px'>
            <Link href={'https://telegram.me/sitiy_support_bot'} target="_blank" rel="noopener nofollow"><IconSocialTelegram/></Link>
            <Link href={'viber://pa?chatURI=sitiy'} target="_blank" rel="noopener nofollow"><IconSocialViber/></Link>
            <Link href={'https://vk.com/sytofis'} target="_blank" rel="noopener nofollow"><IconSocialVk/></Link>
          </Flex>
        </FormFooter>
      </AuthForm>
    </Wrapper>
  );
};

export default LoginForm;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, .75);
`
const AuthForm = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  max-width: 924px;
  min-height: 400px;
  margin: 0 20px;
  padding: 30px 0;
  border-radius: 24px;
  background: #FFFFFF;
  @media (max-width: 998px) {
    grid-template-columns: 1fr;
  }
`
const FormHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin: 0 auto;
  padding: 10px 50px;
  width: 320px;
  & svg {
    width: 100%;
    fill: #EF0A36;
  }
  @media (max-width: 998px) {
    width: 240px;
    padding: 10px;
  }
`
const FormBody = styled.div`
  grid-row: 1 / 3;
  grid-column: 2 / 3;
  padding: 10px 50px;
  border-left: solid 1px black;
  @media (max-width: 998px) {
    grid-row: 2 / 3;
    grid-column: 1 / 2;
    padding: 10px;
    border-left: none;
  }
`
const FormFooter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin: 0 auto;
  padding: 10px 50px;
  width: 320px;
  @media (max-width: 998px) {
    width: 240px;
    padding: 10px;
  }
`
const StyledLink = styled(Link)`
  display: flex;
  gap: 5px;
  margin-bottom: 8px;
  padding: 12px 20px;
  width: 100%;
  background: #f1f1f1;
  border-radius: 16px;
  color: #2C2E32;
`